import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  ChevronDown, ChevronRight, Users, Sparkles, Compass, FileBarChart, Heart,
  GraduationCap, BookOpen, Video, Layers, Target, HelpCircle, LifeBuoy, Mail,
  Home, Baby, Stethoscope, Calculator, Clapperboard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Landing-page mega navigation.
 * Structure matches the user-approved spec exactly:
 *   Home   Features ▾   Pricing   Resources ▾   About ▾   (Support nested inside About)
 *
 * Behaviour:
 *  - Click a trigger to open / toggle its dropdown
 *  - Click outside or press Escape to close
 *  - Hovering the "Support" item inside About reveals a nested flyout
 */
const FEATURES_ITEMS = [
  { icon: Users,        label: "Who We Support",        to: "/#who-we-support" },
  { icon: Sparkles,     label: "Why Kiddo+",            to: "/#features" },
  { icon: Compass,      label: "How It Works",          to: "/#how" },
  { icon: FileBarChart, label: "Developmental Reports", to: "/#report-preview" },
];
const RESOURCES_ITEMS = [
  { icon: GraduationCap, label: "Courses",                to: "/courses" },
  { icon: BookOpen,      label: "Books",                  to: "/books" },
  { icon: Sparkles,      label: "Activities",             to: "/explore-activities" },
  { icon: Video,         label: "Videos",                 to: "/videos" },
  { icon: Layers,        label: "View All Categories",    to: "/resources" },
];

const TOOLS_ITEMS = [
  { icon: Baby,         label: "Development Milestones",   to: "/milestones" },
  { icon: Stethoscope,  label: "Free Screening",           to: "/free-screening" },
  { icon: Calculator,   label: "Calculate Child Age",      to: "/child-age-calculator" },
  { icon: Clapperboard, label: "AI Video Assessment (Beta)", to: "/ai-video-assessment", badge: "Beta" },
];
const ABOUT_ITEMS = [
  { icon: Target, label: "Our Mission", to: "/about#mission" },
  { icon: Users,  label: "Who We Are",  to: "/about" },
];
const SUPPORT_ITEMS = [
  { icon: HelpCircle, label: "FAQs",            to: "/faqs" },
  { icon: LifeBuoy,   label: "Help Center",     to: "/help-center" },
  { icon: Mail,       label: "Contact Support", href: "mailto:support@kiddoplus.com" },
];

export default function MegaNav({ className = "" }) {
  const [openKey, setOpenKey] = useState(null);
  const ref = useRef(null);
  const { pathname, hash } = useLocation();
  const { user } = useAuth();
  // Free Screening is a lead-magnet for anonymous visitors only — logged-in
  // parents already have the full assessment inside the dashboard.
  const toolsItems = user
    ? TOOLS_ITEMS.filter((it) => it.to !== "/free-screening")
    : TOOLS_ITEMS;
  // On the landing page itself the brand logo to the left already routes Home,
  // so we hide the redundant textual Home link there.
  const isLanding = pathname === "/";

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenKey(null); };
    const onKey = (e) => { if (e.key === "Escape") setOpenKey(null); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDocClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const close = () => setOpenKey(null);

  // Compute the active route group so the corresponding mega-nav trigger
  // shows the coral pill even when its dropdown is closed.
  const FEATURES_HASHES = new Set(["#who-we-support", "#features", "#how", "#report-preview"]);
  const RESOURCES_PATHS = new Set(["/courses", "/books", "/videos", "/resources", "/explore-activities"]);
  const TOOLS_PATHS     = new Set(["/milestones", "/free-screening", "/child-age-calculator", "/ai-video-assessment"]);
  const ABOUT_PATHS     = new Set(["/about", "/faqs", "/help-center"]);
  const activeKey =
    pathname === "/" && FEATURES_HASHES.has(hash)  ? "features"  :
    pathname === "/" && hash === "#plans"           ? "pricing"   :
    [...RESOURCES_PATHS].some((p) => pathname === p || pathname.startsWith(p + "/")) ? "resources" :
    [...TOOLS_PATHS].some((p) => pathname === p || pathname.startsWith(p + "/"))     ? "tools"     :
    [...ABOUT_PATHS].some((p) => pathname === p || pathname.startsWith(p + "/"))     ? "about"     :
    null;

  return (
    <nav ref={ref} className={`hidden md:flex items-center gap-1 lg:gap-2 text-sm font-semibold ${className}`} data-testid="landing-mega-nav">
      {!isLanding && (
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `px-3 py-2 rounded-full whitespace-nowrap transition-colors ${isActive ? "text-coral bg-coral/10" : "text-inkmid hover:text-coral hover:bg-coral/10"}`
          }
          data-testid="meganav-home"
        >
          Home
        </NavLink>
      )}

      <Trigger label="Features" active={activeKey === "features"} open={openKey === "features"} onClick={() => setOpenKey(openKey === "features" ? null : "features")} testid="meganav-features-trigger">
        <Panel items={FEATURES_ITEMS} onSelect={close} />
      </Trigger>

      <Link
        to="/#plans"
        onClick={() => { jumpToHash("/#plans"); }}
        className={`px-3 py-2 rounded-full whitespace-nowrap transition-colors ${activeKey === "pricing" ? "text-coral bg-coral/10" : "text-inkmid hover:text-coral hover:bg-coral/10"}`}
        data-testid="meganav-pricing"
      >
        Pricing
      </Link>

      <Trigger label="Resources" active={activeKey === "resources"} open={openKey === "resources"} onClick={() => setOpenKey(openKey === "resources" ? null : "resources")} testid="meganav-resources-trigger">
        <Panel items={RESOURCES_ITEMS} onSelect={close} />
      </Trigger>

      <Trigger label="Free Tools" badge="New" active={activeKey === "tools"} open={openKey === "tools"} onClick={() => setOpenKey(openKey === "tools" ? null : "tools")} testid="meganav-tools-trigger">
        <Panel items={toolsItems} onSelect={close} />
      </Trigger>

      <Trigger label="About" active={activeKey === "about"} open={openKey === "about"} onClick={() => setOpenKey(openKey === "about" ? null : "about")} testid="meganav-about-trigger">
        <AboutPanel onSelect={close} />
      </Trigger>
    </nav>
  );
}

function Trigger({ label, open, active, onClick, testid, badge, children }) {
  const isHot = open || active;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        aria-expanded={open}
        data-testid={testid}
        className={`flex items-center gap-1 px-3 py-2 rounded-full whitespace-nowrap transition-colors ${isHot ? "text-coral bg-coral/10" : "text-inkmid hover:text-coral hover:bg-coral/10"}`}
      >
        {label}
        {badge && (
          <span
            className="kp-badge-new ml-0.5"
            data-testid={`${testid}-badge`}
            aria-label="New"
          >
            {badge}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && children}
    </div>
  );
}

function Panel({ items, onSelect }) {
  return (
    <div className="absolute top-full left-0 mt-2 bg-white rounded-3xl shadow-soft-lg border border-plum/10 p-3 z-50 min-w-[280px]">
      <ul>{items.map((it) => <PanelItem key={it.label} item={it} onSelect={onSelect} />)}</ul>
    </div>
  );
}

function AboutPanel({ onSelect }) {
  const [supportOpen, setSupportOpen] = useState(false);
  return (
    <div
      className="absolute top-full left-0 mt-2 bg-white rounded-3xl shadow-soft-lg border border-plum/10 p-3 z-50 min-w-[280px]"
      onMouseLeave={() => setSupportOpen(false)}
      data-testid="meganav-about-panel"
    >
      <ul>
        {ABOUT_ITEMS.map((it) => <PanelItem key={it.label} item={it} onSelect={onSelect} />)}

        {/* Nested Support flyout — opens on hover OR click; closes via the
            parent panel's onMouseLeave or by clicking outside. We only ever
            *open* on click so hover→click doesn't toggle it back closed. */}
        <li className="relative" onMouseEnter={() => setSupportOpen(true)}>
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            aria-expanded={supportOpen}
            data-testid="meganav-support-trigger"
            className={`group flex items-center gap-3 w-full p-2 rounded-2xl transition-colors ${supportOpen ? "bg-coral/10" : "hover:bg-cream"}`}
          >
            <span className="shrink-0 w-9 h-9 rounded-xl bg-mint/10 text-mint flex items-center justify-center">
              <LifeBuoy className="w-4 h-4" strokeWidth={2.2} />
            </span>
            <span className="flex-1 text-left font-heading text-sm text-inkdark">Support</span>
            <ChevronRight className={`w-4 h-4 text-inkmid transition-transform ${supportOpen ? "translate-x-0.5 text-coral" : ""}`} />
          </button>
          {supportOpen && (
            <div className="absolute top-0 left-full ml-2 bg-white rounded-3xl shadow-soft-lg border border-plum/10 p-3 z-[60] min-w-[240px]" data-testid="meganav-support-panel">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-inkmid px-2 mb-2">Support</p>
              <ul>{SUPPORT_ITEMS.map((it) => <PanelItem key={it.label} item={it} onSelect={onSelect} />)}</ul>
            </div>
          )}
        </li>
      </ul>
    </div>
  );
}

function PanelItem({ item, onSelect }) {
  const Icon = item.icon;
  const hasHash = typeof item.to === "string" && item.to.includes("#");
  const inner = (
    <span className="group flex items-center gap-3 p-2 rounded-2xl hover:bg-cream transition-colors cursor-pointer">
      <span className="shrink-0 w-9 h-9 rounded-xl bg-coral/10 text-coral flex items-center justify-center group-hover:scale-105 transition-transform">
        {Icon && <Icon className="w-4 h-4" strokeWidth={2.2} />}
      </span>
      <span className="flex-1 font-heading text-sm text-inkdark">{item.label}</span>
    </span>
  );
  return (
    <li>
      {item.to ? (
        <Link
          to={item.to}
          onClick={() => { if (hasHash) jumpToHash(item.to); onSelect(); }}
          className="block"
        >{inner}</Link>
      ) : (
        <a
          href={item.href}
          target={item.href?.startsWith("http") ? "_blank" : undefined}
          rel={item.href?.startsWith("http") ? "noopener noreferrer" : undefined}
          onClick={onSelect}
          className="block"
        >
          {inner}
        </a>
      )}
    </li>
  );
}

/**
 * Mirrors MobileDrawerItem.jsx's jumpToHash. Kept duplicated in this file
 * to avoid a cross-file import cycle (MegaNav.jsx and MobileDrawerItem.jsx
 * are both imported by MobileMenuDrawer).
 */
function jumpToHash(to) {
  if (typeof to !== "string") return;
  const i = to.indexOf("#");
  if (i < 0) return;
  const id = to.slice(i + 1);
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;
  try { el.scrollIntoView({ behavior: "auto", block: "start" }); }
  catch {
    const top = el.getBoundingClientRect().top + (window.scrollY || 0);
    window.scrollTo(0, top);
  }
}

/**
 * Items used by the mobile drawer — nested structure that mirrors the
 * desktop dropdowns. Top-level entries with `children` render as
 * collapsible accordions in the drawer.
 */
export const MOBILE_DRAWER_ITEMS = [
  { to: "/", label: "Home", icon: Home, end: true },
  {
    label: "Features",
    icon: Sparkles,
    children: FEATURES_ITEMS,
    defaultOpen: true,
  },
  { to: "/#plans", label: "Pricing", icon: Heart },
  {
    label: "Resources",
    icon: BookOpen,
    children: RESOURCES_ITEMS,
  },
  {
    label: "Free Tools",
    icon: Calculator,
    badge: "New",
    children: TOOLS_ITEMS,
  },
  {
    label: "About",
    icon: Target,
    children: [
      ...ABOUT_ITEMS,
      {
        label: "Support",
        icon: LifeBuoy,
        children: SUPPORT_ITEMS.map((it) => ({ ...it, external: !!it.href })),
      },
    ],
  },
];
